// The Terminal console's pane layout (#4397, epic #4391): a pure binary split
// tree, plus which pane is zoomed and which is focused. No DOM, no signals —
// every operation below is `(TerminalLayout, ...) => TerminalLayout`, so
// `layout.test.ts` proves the whole state machine without a render.
//
// SHAPE. A leaf names a pane by id; a split holds two children (`a`/`b`)
// along one `dir`, `ratio` in `(0, 1)` giving `a`'s share. `dir` is spelled
// `'row'`/`'column'` (flex-direction, not "h"/"v") because Terminator's own
// "Split Horizontally"/"Split Vertically" naming is ambiguous across tools —
// tmux's `-h` means the OPPOSITE arrangement GNOME Terminator's "Horizontal"
// does. `splitHorizontal`/`splitVertical` below are the issue's named ops,
// each pinned to ONE concrete `dir` so the ambiguity resolves once, here, and
// every caller just gets "the new pane appears below" or "beside".
//
// SPLIT NODES HAVE NO ID. A pane id is a stable identity a consumer (a filter,
// a `TerminalLog`) is keyed on; a split is structural chrome with no state of
// its own. `setRatioAtPath` addresses a split by its PATH from the root — the
// sequence of `'a'/'b'` turns a renderer already walks to draw it — rather
// than inventing a second id namespace nothing else needs.
//
// THE NORMALIZER, not three re-derivations of the same invariant. Every
// mutating op funnels through {@link normalize}: `zoomId`/`focusId` are valid
// exactly when they name a LIVE leaf, and `focusId` always names one (there is
// always at least one pane) — "if it isn't a live leaf id, null it" lives in
// one place so a fourth operation added later inherits it for free.

import { z } from 'zod';

export type SplitDirection = 'row' | 'column';

export type LayoutNode =
  | { readonly kind: 'pane'; readonly id: string }
  | {
      readonly kind: 'split';
      readonly dir: SplitDirection;
      /** `a`'s share of the split, `(0, 1)`. `b` takes the remainder. */
      readonly ratio: number;
      readonly a: LayoutNode;
      readonly b: LayoutNode;
    };

export type TerminalLayout = {
  readonly root: LayoutNode;
  /** The one pane rendered full-window, or `null` for the split view. */
  readonly zoomId: string | null;
  /** The pane whose title bar and filter box currently have focus. */
  readonly focusId: string | null;
};

/** A split's ratio is clamped so neither side can collapse to zero width. */
export const MIN_RATIO = 0.1;
export const MAX_RATIO = 0.9;

const clampRatio = (ratio: number): number => Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));

/** Every pane id in the tree, in left-to-right (`a` before `b`) document order. */
export function paneIds(node: LayoutNode): string[] {
  return node.kind === 'pane' ? [node.id] : [...paneIds(node.a), ...paneIds(node.b)];
}

/**
 * The pane after (`1`) or before (`-1`) `fromId` in `paneIds` order, wrapping
 * at both ends — the keyboard's next/previous-pane step. A single-pane layout
 * returns `fromId` itself; an id not in the tree starts from the first pane.
 */
export function nextPaneId(layout: TerminalLayout, fromId: string, direction: 1 | -1): string {
  const ids = paneIds(layout.root);
  const at = ids.indexOf(fromId);
  if (at === -1) return firstPaneId(layout.root);
  return ids[(at + direction + ids.length) % ids.length] ?? fromId;
}

/** The leftmost pane id — the deterministic fallback when focus needs a home. */
export function firstPaneId(node: LayoutNode): string {
  return node.kind === 'pane' ? node.id : firstPaneId(node.a);
}

function hasPane(node: LayoutNode, id: string): boolean {
  return node.kind === 'pane' ? node.id === id : hasPane(node.a, id) || hasPane(node.b, id);
}

/**
 * The one invariant seam: `zoomId`/`focusId` are valid exactly when they name
 * a pane still present in `root`. `focusId` never comes back `null` — a tree
 * always has at least one pane, so an invalid or absent focus falls back to
 * the leftmost one rather than leaving nothing focused.
 */
function normalize(layout: TerminalLayout): TerminalLayout {
  const zoomId =
    layout.zoomId !== null && hasPane(layout.root, layout.zoomId) ? layout.zoomId : null;
  const focusId =
    layout.focusId !== null && hasPane(layout.root, layout.focusId)
      ? layout.focusId
      : firstPaneId(layout.root);
  return zoomId === layout.zoomId && focusId === layout.focusId
    ? layout
    : { ...layout, zoomId, focusId };
}

/** A fresh layout holding exactly one pane, focused. */
export function createTerminalLayout(paneId: string): TerminalLayout {
  return { root: { kind: 'pane', id: paneId }, zoomId: null, focusId: paneId };
}

function replaceNode(
  node: LayoutNode,
  targetId: string,
  build: (found: LayoutNode) => LayoutNode,
): LayoutNode {
  if (node.kind === 'pane') return node.id === targetId ? build(node) : node;
  return {
    ...node,
    a: replaceNode(node.a, targetId, build),
    b: replaceNode(node.b, targetId, build),
  };
}

/**
 * Split `targetId`'s pane into itself plus a new pane `newId`, along `dir`.
 * The target's own id is PRESERVED — it becomes the `a` side, `newId` the
 * `b` side — so a zoom or a filter already keyed on it keeps working after
 * the split. No-op when `targetId` is not a pane in the tree (or `newId`
 * already is one); the new pane takes focus, matching every terminal
 * multiplexer's own convention (you type into the pane you just opened, not
 * the one you split from).
 */
export function splitPane(
  layout: TerminalLayout,
  targetId: string,
  dir: SplitDirection,
  newId: string,
): TerminalLayout {
  if (!hasPane(layout.root, targetId) || hasPane(layout.root, newId)) return layout;
  const root = replaceNode(layout.root, targetId, (found) => ({
    kind: 'split',
    dir,
    ratio: 0.5,
    a: found,
    b: { kind: 'pane', id: newId },
  }));
  return normalize({ ...layout, root, focusId: newId });
}

/** Terminator's "Split Horizontally": the new pane appears BELOW (`column`). */
export function splitHorizontal(
  layout: TerminalLayout,
  targetId: string,
  newId: string,
): TerminalLayout {
  return splitPane(layout, targetId, 'column', newId);
}

/** Terminator's "Split Vertically": the new pane appears BESIDE (`row`). */
export function splitVertical(
  layout: TerminalLayout,
  targetId: string,
  newId: string,
): TerminalLayout {
  return splitPane(layout, targetId, 'row', newId);
}

/**
 * Close `id`'s pane. Closing the tree's last pane is a no-op (there is always
 * at least one). Otherwise the split node holding it collapses: the SIBLING
 * subtree (which may itself be a split, not only a leaf) replaces it wholesale
 * — so closing one pane of a three-way layout leaves the other two exactly as
 * they were arranged relative to each other, not re-flattened. Closing the
 * zoomed pane clears the zoom; closing the focused pane moves focus to the
 * promoted subtree's own leftmost leaf.
 */
export function closePane(layout: TerminalLayout, id: string): TerminalLayout {
  if (layout.root.kind === 'pane') return layout; // the last pane — no-op
  if (!hasPane(layout.root, id)) return layout;

  function collapse(node: LayoutNode): LayoutNode {
    if (node.kind === 'pane') return node;
    if (node.a.kind === 'pane' && node.a.id === id) return node.b;
    if (node.b.kind === 'pane' && node.b.id === id) return node.a;
    return { ...node, a: collapse(node.a), b: collapse(node.b) };
  }

  const root = collapse(layout.root);
  const focusId = layout.focusId === id ? firstPaneId(root) : layout.focusId;
  const zoomId = layout.zoomId === id ? null : layout.zoomId;
  return normalize({ root, zoomId, focusId });
}

/**
 * Toggle `id` as the zoomed (full-window) pane: zooming an already-zoomed
 * pane clears the zoom, zooming a different one replaces it. No-op when `id`
 * is not a pane in the tree.
 */
export function toggleZoom(layout: TerminalLayout, id: string): TerminalLayout {
  if (!hasPane(layout.root, id)) return layout;
  return normalize({ ...layout, zoomId: layout.zoomId === id ? null : id });
}

/** Focus `id`'s pane. No-op when `id` is not a pane in the tree. */
export function setFocus(layout: TerminalLayout, id: string): TerminalLayout {
  if (!hasPane(layout.root, id) || layout.focusId === id) return layout;
  return normalize({ ...layout, focusId: id });
}

/**
 * Set the ratio of the split at `path` (the sequence of `'a'`/`'b'` turns
 * from the root — what a recursive pane-tree renderer already knows about its
 * own divider). Clamped to `[MIN_RATIO, MAX_RATIO]`; a path that does not
 * resolve to a split node, or resolves past a leaf, is a no-op.
 */
export function setRatioAtPath(
  layout: TerminalLayout,
  path: readonly ('a' | 'b')[],
  ratio: number,
): TerminalLayout {
  function set(node: LayoutNode, rest: readonly ('a' | 'b')[]): LayoutNode | null {
    if (rest.length === 0) {
      return node.kind === 'split' ? { ...node, ratio: clampRatio(ratio) } : null;
    }
    if (node.kind === 'pane') return null;
    const [turn, ...tail] = rest;
    const side = turn === 'a' ? set(node.a, tail) : set(node.b, tail);
    if (side === null) return null;
    return turn === 'a' ? { ...node, a: side } : { ...node, b: side };
  }
  const root = set(layout.root, path);
  return root === null ? layout : { ...layout, root };
}

// ─── persistence shape ────────────────────────────────────────────────────

const LayoutNodeSchema: z.ZodType<LayoutNode> = z.lazy(() =>
  z.union([
    z.object({ kind: z.literal('pane'), id: z.string().min(1) }),
    z.object({
      kind: z.literal('split'),
      dir: z.union([z.literal('row'), z.literal('column')]),
      ratio: z.number(),
      a: LayoutNodeSchema,
      b: LayoutNodeSchema,
    }),
  ]),
);

/** The shape `dz-terminal-layout` is stored under — validated, not merely typed. */
export const TerminalLayoutSchema: z.ZodType<TerminalLayout> = z.object({
  root: LayoutNodeSchema,
  zoomId: z.string().nullable(),
  focusId: z.string().nullable(),
});

/**
 * A stored layout, re-validated and RE-NORMALIZED — the ratio is re-clamped
 * (a hand-edited or previously-buggy value could sit outside the bounds this
 * module now enforces) and `zoomId`/`focusId` are re-checked against the tree
 * that was actually parsed, exactly like a fresh operation's own result.
 */
export function normalizeLoadedLayout(layout: TerminalLayout): TerminalLayout {
  function clampNode(node: LayoutNode): LayoutNode {
    return node.kind === 'pane'
      ? node
      : { ...node, ratio: clampRatio(node.ratio), a: clampNode(node.a), b: clampNode(node.b) };
  }
  return normalize({ ...layout, root: clampNode(layout.root) });
}
